/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    GetOnePassByUnifiedCollectionIdPreviewApi,
    OnePassByUnifiedCollectionIdPreviewGetRequest,
    OnepassesByUnifiedCollectionIdOptionsGetRequest,
    OnepassesByUnifiedCollectionIdScheduleDeleteRequest,
    OnepassesByUnifiedCollectionIdScheduleGetRequest,
    OnepassesByUnifiedCollectionIdSchedulePostRequest,
    OnepassesByUnifiedCollectionIdSchedulePutRequest,
    OnepassItemsGetRequest,
    OnePassValues,
} from 'client-code/onepass';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IOnePassServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import {
    TOnePassList,
    TOnePassListParams,
    TOnePassOptions,
    TOnePassPreviewItem,
    TOnePassUCIdPreivewParams,
    TOnePassValuesWithDisplayOptions,
    TServicesParams,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class OnePassServiceRepositoryImpl implements IOnePassServiceRepository {
    private static TAG: string = 'OnePassServiceRepositoryImpl';

    private mCommonHeaders: CommonHeaders;

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;
        this.mCommonHeaders = new CommonHeaders('all');
    }

    private createOnePassApi(): DefaultApi {
        const configurationParameters = this.getConfigurationParameters();
        const configuration = new Configuration(configurationParameters);
        return new DefaultApi(configuration);
    }

    private getConfigurationParameters() {
        const configurationParameters: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };
        return configurationParameters;
    }

    private getCommonRequestParameters() {
        const {
            ApplicationName,
            DeviceType,
            ProductName,
            ApplicationFeatureArea,
            ApplicationVersion,
        } = this.mCommonHeaders.headers;

        return {
            applicationName: ApplicationName,
            applicationFeatureArea: ApplicationFeatureArea,
            applicationVersion: ApplicationVersion,
            deviceType: DeviceType,
            productName: ProductName,
            bodyId: this.mServiceParams.bodyId,
            timeZoneName: this.mServiceParams.timeZone,
            acceptLanguage: this.mCommonHeaders.headers['Accept-Language'],
            xAmznRequestid: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * @inheritdoc
     */
    public async createOnePass(
        unifiedCollectionId: string,
        onePassValues: OnePassValues,
        networkCallId?: number,
    ): Promise<void> {
        try {
            const onePassApi = this.createOnePassApi();
            const requestParameters: OnepassesByUnifiedCollectionIdSchedulePostRequest = {
                ...this.getCommonRequestParameters(),
                unifiedCollectionId,
                onePassValues,
            };

            await onePassApi.onepassesByUnifiedCollectionIdSchedulePost(requestParameters);
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::createOnePass',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async deleteOnePass(unifiedCollectionId: string, networkCallId?: number): Promise<void> {
        try {
            const onePassApi = this.createOnePassApi();
            const requestParameters: OnepassesByUnifiedCollectionIdScheduleDeleteRequest = {
                ...this.getCommonRequestParameters(),
                unifiedCollectionId,
            };

            await onePassApi.onepassesByUnifiedCollectionIdScheduleDelete(requestParameters);
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::deleteOnePass',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line max-len
    public async getOnePassList(onePassListParams: TOnePassListParams): Promise<TOnePassList> {
        try {
            const { count, offset, orderBy, networkCallId, whiny } = onePassListParams;
            const onePassApi = this.createOnePassApi();
            const requestParameters: OnepassItemsGetRequest = {
                ...this.getCommonRequestParameters(),
                xAmznRequestid: UniqueIdentifier.generateXAmznRequestId(),
                ...(count && { count }),
                ...(offset && { offset }),
                ...(orderBy && { orderBy }),
                ...(whiny && { whiny }),
            };
            const onePassList = await onePassApi.onepassItemsGet(requestParameters);
            return {
                networkCallId,
                onePassItems: onePassList.data || [],
                totalCount: Number(onePassList.headers['x-total-count']),
            };
        } catch (error: any) {
            error.networkCallId = onePassListParams.networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::getOnePassList',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async getOnePassOptions(
        unifiedCollectionId: string,
        networkCallId?: number,
        channelId?: string,
        contentId?: string,
    ): Promise<TOnePassOptions> {
        try {
            const onePassApi = this.createOnePassApi();
            const requestParameters: OnepassesByUnifiedCollectionIdOptionsGetRequest = {
                ...this.getCommonRequestParameters(),
                unifiedCollectionId,
                channelId,
                contentId,
            };

            const onePassOptions =
                await onePassApi.onepassesByUnifiedCollectionIdOptionsGet(requestParameters);
            return { networkCallId, onePassOptions: onePassOptions.data };
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::getOnePassOptions',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async getValuesWithDisplayOptions(
        unifiedCollectionId: string,
        networkCallId?: number,
        channelId?: string,
        contentId?: string,
    ): Promise<TOnePassValuesWithDisplayOptions> {
        try {
            const onePassApi = this.createOnePassApi();
            const requestParameters: OnepassesByUnifiedCollectionIdScheduleGetRequest = {
                unifiedCollectionId,
                ...this.getCommonRequestParameters(),
                channelId,
                contentId,
            };

            const onePassValuesWithDisplayOptions = (
                await onePassApi.onepassesByUnifiedCollectionIdScheduleGet(requestParameters)
            ).data;
            return {
                networkCallId,
                onePassValuesWithDisplayOptions,
            };
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::getValuesWithDisplayOptions',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async updateOnePass(
        unifiedCollectionId: string,
        onePassValues: OnePassValues,
        networkCallId?: number,
    ): Promise<void> {
        try {
            const onePassApi = this.createOnePassApi();
            const requestParameters: OnepassesByUnifiedCollectionIdSchedulePutRequest = {
                ...this.getCommonRequestParameters(),
                unifiedCollectionId,
                onePassValues,
            };

            await onePassApi.onepassesByUnifiedCollectionIdSchedulePut(requestParameters);
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::updateOnePass',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async getOnePassPreviewItemByUCId(
        onePassUCIdPreivewParams: TOnePassUCIdPreivewParams,
    ): Promise<TOnePassPreviewItem> {
        try {
            this.mCommonHeaders = new CommonHeaders(onePassUCIdPreivewParams.screenType);

            const requestParameters: OnePassByUnifiedCollectionIdPreviewGetRequest = {
                ...this.getCommonRequestParameters(),
                unifiedCollectionId: onePassUCIdPreivewParams.unifiedCollectionId,
                includeChannel: onePassUCIdPreivewParams.includeChannel,
            };

            const configuration = new Configuration(this.getConfigurationParameters());
            const onePassByUCIdPreviewApi = new GetOnePassByUnifiedCollectionIdPreviewApi(
                configuration,
            );

            const onePassPreview = (
                await onePassByUCIdPreviewApi.onePassByUnifiedCollectionIdPreviewGet(
                    requestParameters,
                )
            ).data;

            return {
                networkCallId: onePassUCIdPreivewParams.networkCallId,
                preview: onePassPreview,
            };
        } catch (error: any) {
            error.networkCallId = onePassUCIdPreivewParams.networkCallId;
            Log.e(
                OnePassServiceRepositoryImpl.TAG,
                'OnePassServiceRepositoryImpl::getOnePassByUnifiedCollectionIdPreview',
                error,
            );
            throw error;
        }
    }
}
